import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { PageHeader, DarkPanel, dark } from '../components/ui';

function startOfWeek(d = new Date()) {
  const x = new Date(d);
  const day = x.getDay(); // 0 Sun
  const diff = day === 0 ? -6 : 1 - day; // Monday start
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** YYYY-MM-DD from LOCAL date parts (toISOString used UTC and shifted the day for
 *  users east of UTC). */
function fmt(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function hourLabel(h) {
  if (!h) return '—';
  const v = String(h).toLowerCase();
  if (v === 'bmo') return 'Before open';
  if (v === 'amc') return 'After close';
  if (v === 'dmh') return 'During market';
  return h;
}

/** US / NYSE market holidays (fixed + observed common dates 2024–2027) */
function isNyseHoliday(dateStr) {
  const holidays = new Set([
    // 2024
    '2024-01-01', '2024-01-15', '2024-02-19', '2024-03-29', '2024-05-27',
    '2024-06-19', '2024-07-04', '2024-09-02', '2024-11-28', '2024-12-25',
    // 2025
    '2025-01-01', '2025-01-20', '2025-02-17', '2025-04-18', '2025-05-26',
    '2025-06-19', '2025-07-04', '2025-09-01', '2025-11-27', '2025-12-25',
    // 2026
    '2026-01-01', '2026-01-19', '2026-02-16', '2026-04-03', '2026-05-25',
    '2026-06-19', '2026-07-03', '2026-09-07', '2026-11-26', '2026-12-25',
    // 2027
    '2027-01-01', '2027-01-18', '2027-02-15', '2027-03-26', '2027-05-31',
    '2027-06-18', '2027-07-05', '2027-09-06', '2027-11-25', '2027-12-24',
  ]);
  return holidays.has(dateStr);
}

export default function EarningsCalendar() {
  const [weekStart, setWeekStart] = useState(() => startOfWeek());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const from = fmt(weekStart);
  const to = fmt(addDays(weekStart, 6));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    api.get(`/stock/earnings-calendar?from=${from}&to=${to}`)
      .then(r => {
        if (cancelled) return;
        setData(r.data);
        if (!r.data?.success) setError(r.data?.message || 'Could not load earnings');
      })
      .catch(() => {
        if (!cancelled) setError('Could not load earnings calendar');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [from, to]);

  const byDay = useMemo(() => {
    const map = {};
    // Only Mon–Fri trading days (skip Sat/Sun)
    for (let i = 0; i < 5; i++) {
      map[fmt(addDays(weekStart, i))] = [];
    }
    const rows = data?.earnings || [];
    for (const e of rows) {
      const d = e.date;
      if (!map[d]) continue; // ignore weekend rows if any
      map[d].push(e);
    }
    // Real data only — no made-up "notable" filler names on quiet days
    Object.keys(map).forEach((key) => { if (isNyseHoliday(key)) map[key] = []; });
    return map;
  }, [data, weekStart]);

  const days = Object.keys(byDay);

  const thisWeek = fmt(startOfWeek()) === from;

  return (
    <div className="pb-8">
      <PageHeader
        title="Earnings Calendar"
        subtitle={`Week of ${from} → ${fmt(addDays(weekStart, 4))} · Trading days · Large-cap focus`}
      />
      <div className="mb-4 flex flex-wrap gap-1 rounded-xl bg-slate-100 p-1 w-fit">
        {[
          ['prev', '← Prev week', () => setWeekStart(addDays(weekStart, -7))],
          ['this', 'This week', () => setWeekStart(startOfWeek())],
          ['next', 'Next week →', () => setWeekStart(addDays(weekStart, 7))],
        ].map(([key, label, onClick]) => (
          <button
            key={key}
            type="button"
            onClick={onClick}
            className={`rounded-lg px-3 py-1.5 text-xs transition ${
              key === 'this' && thisWeek ? 'bg-slate-900 font-semibold text-white' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading && (
        <p className="text-center text-slate-400 py-16">Loading earnings…</p>
      )}

      {!loading && error && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-900 text-sm px-4 py-3 mb-4">
          {error}
        </div>
      )}

      {!loading && (
        <div className="space-y-4">
          {days.map(day => {
            const list = byDay[day] || [];
            const holiday = isNyseHoliday(day);
            const label = new Date(day + 'T12:00:00').toLocaleDateString(undefined, {
              weekday: 'short', month: 'short', day: 'numeric',
            });
            return (
              <DarkPanel
                key={day}
                title={label}
                subtitle={holiday ? 'Market holiday' : undefined}
                right={<span className="text-xs text-slate-400">{holiday ? 'Closed' : `${list.length} stocks`}</span>}
                bodyClassName="p-0"
              >
                {holiday ? (
                  <p className="px-4 py-4 text-xs text-slate-400">NYSE closed — no regular session</p>
                ) : list.length === 0 ? (
                  <p className="px-4 py-4 text-xs text-slate-400">No large-cap earnings scheduled</p>
                ) : (
                  <div className={dark.wrap}>
                    <table className={dark.table}>
                      <thead className={dark.thead}>
                        <tr>
                          <th className={`${dark.th} text-left`}>Symbol</th>
                          <th className={`${dark.th} text-left`}>When</th>
                          <th className={`${dark.th} text-right`}>EPS est.</th>
                          <th className={`${dark.th} text-right`}>EPS act.</th>
                          <th className={`${dark.th} text-right hidden sm:table-cell`}>Mkt cap</th>
                          <th className={`${dark.th} text-right hidden md:table-cell`}>Quarter</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.map((e, i) => (
                          <tr key={`${e.symbol}-${e.date}-${i}`} className={dark.tr}>
                            <td className={`${dark.td} font-bold`}>
                              <Link to={`/stock/${encodeURIComponent(e.symbol)}`} className="text-white hover:text-emerald-300">
                                {e.symbol}
                              </Link>
                              {e.name && <p className="truncate text-[11px] font-normal text-slate-400">{e.name}</p>}
                            </td>
                            <td className={`${dark.td} text-slate-300`}>{hourLabel(e.hour)}</td>
                            <td className={`${dark.td} text-right tabular-nums`}>
                              {e.epsEstimate != null ? Number(e.epsEstimate).toFixed(2) : '—'}
                            </td>
                            <td className={`${dark.td} text-right tabular-nums`}>
                              {e.epsActual != null ? Number(e.epsActual).toFixed(2) : '—'}
                            </td>
                            <td className={`${dark.td} text-right tabular-nums hidden sm:table-cell text-slate-400`}>
                              {e.marketCapBillions != null ? `$${Number(e.marketCapBillions).toFixed(1)}B` : '—'}
                            </td>
                            <td className={`${dark.td} text-right text-slate-400 hidden md:table-cell text-xs`}>
                              {e.fiscalQuarter || (e.quarter ? `Q${e.quarter} ${e.year || ''}` : '—')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </DarkPanel>
            );
          })}
        </div>
      )}
    </div>
  );
}